import React from "react";
import type { ViewComponent } from "@frui.ts/views";
import { registerView } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import CustomerRefundCandidatesViewModel from "viewModels/finance/customerRefunds/customerRefundCandidates/customerRefundCandidatesViewModel";
import ListView from "../../../common/list";
import type { ColumnDefinition } from "@frui.ts/dataviews/dist/dataTypes";
import type CustomerRefundCandidateResponse from "entities/customerRefundCandidateResponse";
import { formatAmount, formatLicensePlate } from "utils/localizations";
import CustomerRefundCandidatesFilter from "./customerRefundCandidatesFilter";
import Button from "@edaz/edazkit/Button";
import CustomerRefundType from "entities/customerRefundType";
import Tooltip from "@edaz/edazkit/Tooltip";
import classNames from "classnames";
import { Check, CollectionCheck } from "@frui.ts/bootstrap";
import Icon, { IconType } from "@edaz/edazkit/Icon";
import { emptyValue } from "utils/emptyValue";

// eslint-disable-next-line sonarjs/cognitive-complexity
export const CustomerRefundCandidatesView: ViewComponent<CustomerRefundCandidatesViewModel> = observer(({ vm }) => {
  const columns: ColumnDefinition<CustomerRefundCandidateResponse>[] = [
    {
      title: (
        <Check
          custom
          target={vm as { allItems?: boolean }}
          property="allItems"
          threeState={true}
          disabled={!vm.isUserAllowedToEdit}
        />
      ),
      valueFormatter: props => {
        if (props.item.isSelectable) {
          return (
            <CollectionCheck custom target={vm} property="selectedItems" disabled={!vm.isUserAllowedToEdit} value={props.item} />
          );
        }
      },
    },
    {
      property: "charge",
      title: vm.translateGeneral("licensePlate"),
      valueFormatter: ({ item }) => {
        if (item.charge.wasVehicleChangedDuringValidity) {
          return (
            <Tooltip id="license-plate-warning" tooltip={vm.translate("changesAfterValidityTooltip")}>
              <span className="license-plate-warning">
                <span>{formatLicensePlate(item.charge.vehicle)}</span>
                <Icon icon={IconType.CircleWarning} width={22} height={22} />
              </span>
            </Tooltip>
          );
        }

        return formatLicensePlate(item.charge.vehicle);
      },
    },
    {
      title: vm.translateAttribute("variableSymbol"),
      property: "variableSymbol",
    },
    {
      title: vm.translateAttribute("originalPaymentAmount"),
      property: "originalPaymentAmount",
      valueFormatter: props => (props.value !== undefined ? formatAmount(props.value) : emptyValue),
    },
    {
      title: vm.translateAttribute("refundPaymentType"),
      property: "refundPaymentType",
      valueFormatter: props => vm.refundPaymentType(props.value),
    },
    {
      title: vm.translateAttribute("officialRecordId"),
      property: "officialRecordId",
    },
    {
      title: vm.translateAttribute("refundAmount"),
      property: "refundAmount",
      valueFormatter: props => formatAmount(props.value),
    },
  ];

  const actionButtons = (item: CustomerRefundCandidateResponse) => {
    const onDetailClick = vm.onItemClick();
    const refundAllowed = vm.isUserAllowedToEdit ? item.permittedActions.refundation.isAllowed : false;
    const rejectionReason = item.permittedActions.refundation.rejectionReason;

    // We need to fake disabled state, because Bootstrap doesn't propagate events on disabled buttons
    const RefundButton = ({ disabled }: { disabled?: boolean }) => (
      <Button
        variant="primary"
        size="sm"
        className={classNames("rounded", { disabled })}
        onClick={() => (disabled ? null : vm.onGenerateRefundClick(item))}>
        {vm.translate(item.refundType === CustomerRefundType.CustomerPartial ? "generateAndSubmit" : "generateRefund")}
      </Button>
    );
    const RejectButton = ({ disabled }: { disabled?: boolean }) => (
      <Button
        variant="danger"
        size="sm"
        className={classNames("rounded", { disabled })}
        onClick={() => (disabled ? null : vm.onRejectionClick(item))}>
        {vm.translate("reject")}
      </Button>
    );

    return (
      <>
        <Button variant="success" size="sm" className="rounded" onClick={() => (onDetailClick ? onDetailClick(item) : "")}>
          {vm.translate("showDetail")}
        </Button>
        {refundAllowed ? (
          <RefundButton />
        ) : (
          <Tooltip
            tooltip={<div style={{ padding: "5px 0" }}>{vm.translate(`rejection.${rejectionReason}`)}</div>}
            trigger={["hover", "focus"]}
            id={`rejection-detail-${item.id}`}>
            <div className="kit__button">
              <RefundButton disabled={!refundAllowed} />
            </div>
          </Tooltip>
        )}
        {vm.isUserAllowedToEdit ? (
          <RejectButton />
        ) : (
          <Tooltip
            tooltip={<div style={{ padding: "5px 0" }}>{vm.translate("rejection.undefined")}</div>}
            trigger={["hover", "focus"]}
            id={`candidate-rejection-detail-${item.id}`}>
            <div className="kit__button">
              <RejectButton disabled={true} />
            </div>
          </Tooltip>
        )}
      </>
    );
  };

  const generateAllButton = vm.isUserAllowedToEdit ? (
    <Button size="sm" key="export-approved-internal-transfers" variant="primary" className="rounded" onClick={vm.generateAll}>
      {vm.translateGeneral("generateAll")}
    </Button>
  ) : (
    <Tooltip
      key="export-approved-internal-transfers"
      tooltip={<div style={{ padding: "5px 0" }}>{vm.translate(`rejection.undefined`)}</div>}
      trigger={["hover", "focus"]}
      id="generate-all-button">
      <div className="kit__button">
        <Button size="sm" variant="primary" className="rounded disabled" onClick={() => null}>
          {vm.translateGeneral("generateAll")}
        </Button>
      </div>
    </Tooltip>
  );

  const generateSelectedButton = (
    <Button
      size="sm"
      key="approve-selected-refund-candidates"
      variant="primary"
      className="rounded"
      onClick={vm.onApproveSelectedClick}>
      {vm.translate("confirmSelectedApproval.confirm")}
    </Button>
  );

  const headerButtons = [generateAllButton];
  // Add Button for generating selected items
  if (vm.self.selectedItems.size > 0) {
    headerButtons.unshift(generateSelectedButton);
  }

  return (
    <ListView
      vm={vm}
      columns={columns}
      aboveTable={<CustomerRefundCandidatesFilter vm={vm} />}
      showDetailWithList={true}
      headerButtons={headerButtons}
      actionButtons={actionButtons}
    />
  );
});

registerView(CustomerRefundCandidatesView, CustomerRefundCandidatesViewModel);
